import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import axiosInstance from '../../../redux/api';
import { getStatusList } from '../../../redux/Order/action';
import { getAllHeaderCount } from '../../../redux/booking/action';
import { usePermissions } from '../../../Hooks/usePermissions';
import { useOrder } from '../../../Hooks/useOrder';
import { useOrderEditing } from '../../../Hooks/useOrderEditing';
import { MENU, PERMISSION } from '../../../lib/permissions';
import { useToasts, ToastStack } from '../../Common/Toast/useToasts';

import OrderCommandBar from './detail/OrderCommandBar';
import OrderLifecycle from './detail/OrderLifecycle';
import OrderItemsTable from './detail/OrderItemsTable';
import OrderTimeline from './detail/OrderTimeline';
import PaymentCard from './detail/PaymentCard';
import CustomerCard from './detail/CustomerCard';
import DeliveryCard from './detail/DeliveryCard';
import InternalCard from './detail/InternalCard';
import ConfirmDialog from './detail/ConfirmDialog';
import TrackingPromptDialog, { trackingPromptCopy } from './detail/TrackingPromptDialog';
import HoldBanner from './detail/HoldBanner';
import AddItemDialog from './detail/AddItemDialog';
import { OrderViewError, OrderViewSkeleton } from './detail/OrderViewStates';

import { ORDER_STATUS } from './shared/orderConstants';
import {
  COURIER_LABEL,
  SHIPMENT_DIRECTION,
  tripDirectionForStatus,
  tripNumberOnFile,
} from './shared/orderShipments';
import {
  allowedTransitions,
  buildLifecycle,
  getNextLifecycleStep,
  isDestructiveStatus,
  isExitStatus,
  isHoldStatus,
  isReturnStatus,
} from './shared/orderStatus';
import { formatAmount } from './shared/orderFormat';
import {
  canEnterHoldFrom,
  resolveEditability,
} from './shared/orderEditRules';
import './OrderViewStyle.css';

/*
 * Order detail.
 *
 * This file is the orchestrator: data, derived values, and the mutations. All
 * presentation lives in ./detail. Splitting it that way is what lets the items
 * table be memoised and the text inputs own their own drafts, so typing a
 * tracking number no longer re-renders every row.
 *
 * Layout follows Shopify's resource-details pattern, which the admin's other
 * screens already imply: the primary two-thirds carries what defines the order
 * (its line items, then its history), and the secondary third carries status,
 * metadata and summaries.
 *
 * Writes are optimistic. Reversible ones commit immediately and offer Undo in a
 * toast; irreversible ones go through a confirmation first.
 */

// The parcel's trips, each with its own tracking number. Every reply from
// these endpoints carries the order's full `shipments` list and the mirrored
// `tracking_number`, so the screen redraws from the server's answer.
const SHIPMENTS_PATH = 'admin/orders/shipments';

const shipmentNoun = (direction) =>
  direction === SHIPMENT_DIRECTION.RETURN
    ? 'Return tracking number'
    : `${COURIER_LABEL} tracking number`;

const readApiError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.response?.message ||
  fallback;

const OrderView = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasAction, isSuperAdmin } = usePermissions();

  const { order, isLoading, isError, error, reload, patchLocal } =
    useOrder(orderId);

  // The status list is genuinely shared reference data, so it stays in redux —
  // only the order record itself moved to route-local state.
  const statusList = useSelector((state) => state.reusableSlice.StatusList);

  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();

  const [pendingStatusId, setPendingStatusId] = useState(null);
  const [pendingItemIds, setPendingItemIds] = useState(() => new Set());
  const [confirmState, setConfirmState] = useState(null);
  const [isAddItemOpen, setAddItemOpen] = useState(false);
  // A status move that starts a trip (handed to shipping, returned) asks
  // for the trip's tracking number first: { statusId, direction }.
  const [trackingPrompt, setTrackingPrompt] = useState(null);
  const [isTrackingPromptPending, setTrackingPromptPending] = useState(false);

  // Handlers read the order through a ref so their identity stays stable across
  // optimistic patches. Without this, every local update would produce a new
  // onItemStatusChange and defeat the memoised rows.
  const orderRef = useRef(order);
  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  useEffect(() => {
    dispatch(getStatusList());
  }, [dispatch]);

  /* ---------- derived ---------- */

  // `retired` rides along so a status that still names old orders (Rejected)
  // keeps its label everywhere without ever being offered as a destination.
  const statusOptions = useMemo(
    () =>
      Array.isArray(statusList)
        ? statusList.map((item) => ({
            value: item?.id,
            label: item?.title,
            retired: Boolean(item?.retired),
          }))
        : [],
    [statusList],
  );

  // O(1) label lookups. The old code ran `.find()` per row per render.
  const statusLabelById = useMemo(() => {
    const map = new Map();
    statusOptions.forEach((option) => map.set(option.value, option.label));
    return map;
  }, [statusOptions]);

  const labelForStatus = useCallback(
    (statusId) => statusLabelById.get(statusId) || 'Unknown',
    [statusLabelById],
  );

  // The stepper's stages and their names both come from `admin/statuses`, so
  // the track, the header pill and the per-item pickers can never disagree
  // about what a status is called.
  const lifecycle = useMemo(() => buildLifecycle(statusOptions), [statusOptions]);

  const orderItems = useMemo(() => order?.order_items || [], [order]);

  const derived = useMemo(() => {
    const orderTotal = Number(order?.final_price) || 0;
    const deliveryCharge = Number(order?.delivery_charge) || 0;

    return {
      deliveryCharge,
      // Delivery is billed on top of the merchandise and folded into
      // final_price, so the merchandise figure is recovered by removing it
      // rather than summed from the rows. That keeps Subtotal + Delivery =
      // Total true by construction — the identity the PDF invoice prints.
      merchandiseSubtotal: orderTotal - deliveryCharge,
      couponDiscount: Number(order?.coupon_discount_amount) || 0,
      instantDiscount: Number(order?.instant_discount_amount) || 0,
      unitCount: orderItems.reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0,
      ),
      hasB2B: orderItems.some((item) => Number(item.is_b2b) === 1),
    };
  }, [order, orderItems]);

  /*
   * Hold gating.
   *
   * The backend restricts hold to super admins and freezes a held order for
   * everyone else — including edits that carry no status change at all. So for
   * a non-super-admin, a held order is read-only end to end: statuses, item
   * statuses, tracking, and every inline field editor. Leaving those
   * controls enabled would just produce 422s nobody can act on.
   */
  const isHeld = isHoldStatus(order?.status);
  const canManageHold = isSuperAdmin;
  const isLockedByHold = isHeld && !canManageHold;
  const isEditable = hasAction(MENU.ORDER, PERMISSION.EDIT) && !isLockedByHold;

  /*
   * Whether the order's *contents* can change.
   *
   * Stricter than the backend: the house rule is that an order must be put on
   * hold before it can be edited, so the pause is deliberate and visible to
   * everyone else while it's being worked on. See orderEditRules for why that
   * is a UI policy rather than an API guarantee, and for the one awkward path
   * (an out-for-delivery order has to go back to Confirmed before it can be
   * held).
   */
  const editability = useMemo(
    () =>
      resolveEditability({
        order,
        hasEditPermission: hasAction(MENU.ORDER, PERMISSION.EDIT),
        isSuperAdmin,
      }),
    [order, hasAction, isSuperAdmin],
  );

  // Only transitions the server will accept — a held order can be resumed or
  // exited, never advanced, and only a super admin may place one on hold.
  const transitionOptions = useMemo(
    () =>
      allowedTransitions({
        fromStatus: order?.status,
        statusOptions,
        canHold: canManageHold,
      }),
    [order?.status, statusOptions, canManageHold],
  );

  const shipments = useMemo(() => order?.shipments || [], [order]);
  const nextStep = getNextLifecycleStep(order?.status, lifecycle);

  // Timestamps for the stepper. created_on covers Placed; any history entry
  // that carries its own status id fills in the rest, and steps with no known
  // time simply render a dash.
  const stepTimestamps = useMemo(() => {
    const map = { [ORDER_STATUS.PLACED]: order?.created_on };
    (order?.order_status || []).forEach((event) => {
      const id = event?.status ?? event?.status_id;
      if (id != null && event?.created_on) map[id] = event.created_on;
    });
    return map;
  }, [order]);

  /* ---------- mutations ---------- */

  /*
   * Resuming an order doesn't resume its lines.
   *
   * Holding an order cascades down — the backend puts every item on hold too,
   * including any added while it was held. Resuming does not cascade back, so
   * the order reads Confirmed while every line inside it still reads On hold.
   *
   * Fixed here because it's the frontend that can see the inconsistency: after
   * a successful move out of hold, each still-held line is set to the status the
   * order landed on. Only lines actually on hold are touched, which leaves
   * cancelled and delivered ones — that the backend refuses to change — alone.
   *
   * Cost: one request per held line, run sequentially so a large order queues
   * rather than bursts. The durable fix is the backend cascading on resume the
   * way it already cascades on hold; this then becomes a no-op and can be
   * deleted, since it only ever acts on lines that shouldn't still be held.
   *
   * A failure is swallowed deliberately — the reload that follows shows whatever
   * the server actually holds, rather than claiming a state we didn't achieve.
   */
  const resumeHeldItems = useCallback(
    async (nextStatusId) => {
      const current = orderRef.current;
      const held = (current?.order_items || []).filter((item) =>
        isHoldStatus(item.status),
      );
      if (held.length === 0) return 0;

      let resumed = 0;
      for (const item of held) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await axiosInstance.patch('admin/orders/detail-status', {
            status: nextStatusId,
            order_detail_id: item.id,
          });
          resumed += 1;
        } catch {
          /* left as-is; the reload below reports the true state */
        }
      }
      await reload({ quiet: true });
      return resumed;
    },
    [reload],
  );

  const applyOrderStatus = useCallback(
    async (nextStatusId, { undoable = true } = {}) => {
      const current = orderRef.current;
      if (!current || nextStatusId == null) return false;
      const previous = current.status;
      if (previous === nextStatusId) return true;

      setPendingStatusId(nextStatusId);
      patchLocal({ status: nextStatusId });

      try {
        await axiosInstance.patch('admin/orders/status', {
          id: current.order_id,
          status: nextStatusId,
        });
        dispatch(getAllHeaderCount());

        const leavingHold =
          isHoldStatus(previous) && !isHoldStatus(nextStatusId);
        const resumedCount = leavingHold
          ? await resumeHeldItems(nextStatusId)
          : 0;
        // The backend moves every line with the order (a return comes back
        // to life, a cancel strikes through), so the rows are re-read rather
        // than left showing the old line statuses until a refresh.
        if (!leavingHold) await reload({ quiet: true });

        pushToast(
          resumedCount > 0
            ? `Order marked ${labelForStatus(nextStatusId)} · ${resumedCount} ${resumedCount === 1 ? 'item' : 'items'} resumed.`
            : `Order marked ${labelForStatus(nextStatusId)}.`,
          undoable
            ? {
                undo: () =>
                  applyOrderStatus(previous, { undoable: false }),
              }
            : undefined,
        );
        return true;
      } catch (err) {
        patchLocal({ status: previous });
        pushToast(readApiError(err, 'Could not update the status.'), {
          tone: 'danger',
        });
        return false;
      } finally {
        setPendingStatusId(null);
      }
    },
    [dispatch, labelForStatus, patchLocal, pushToast, reload, resumeHeldItems],
  );

  /**
   * Cancelling and returning are not reversible from here, so they ask first,
   * each in its own words. Everything else commits straight away with an Undo
   * in the toast.
   */
  const requestOrderStatus = useCallback(
    (nextStatusId) => {
      // A move that starts a trip asks for the trip's number first, unless
      // the newest trip on file already goes that way (see
      // TrackingPromptDialog). The number is saved before the status.
      const direction = tripDirectionForStatus(nextStatusId);
      if (direction && !tripNumberOnFile(orderRef.current?.shipments, direction)) {
        setTrackingPrompt({ statusId: nextStatusId, direction });
        return;
      }
      if (!isDestructiveStatus(nextStatusId)) {
        applyOrderStatus(nextStatusId);
        return;
      }
      const orderNumber = orderRef.current?.order_number || '';
      if (isReturnStatus(nextStatusId)) {
        setConfirmState({
          title: 'Mark this order as returned?',
          description: `Order #${orderNumber} will be marked ${labelForStatus(
            nextStatusId,
          )} and its items go back into stock. From there you can send it out again from the status menu, or cancel it — the cancel is what releases the payment and any loyalty points.`,
          confirmLabel: 'Mark as returned',
          run: () => applyOrderStatus(nextStatusId, { undoable: false }),
        });
        return;
      }
      setConfirmState({
        title: 'Cancel this order?',
        description: `Order #${orderNumber} will be marked ${labelForStatus(
          nextStatusId,
        )}. This cannot be undone from here, and any payment must be refunded separately.`,
        confirmLabel: 'Cancel order',
        run: () => applyOrderStatus(nextStatusId, { undoable: false }),
      });
    },
    [applyOrderStatus, labelForStatus],
  );

  const markItemPending = useCallback((itemId, pending) => {
    setPendingItemIds((current) => {
      const next = new Set(current);
      if (pending) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  }, []);

  const patchItemStatus = useCallback(
    (itemId, statusId) =>
      patchLocal((current) => ({
        ...current,
        order_items: (current.order_items || []).map((item) =>
          item.id === itemId ? { ...item, status: statusId } : item,
        ),
      })),
    [patchLocal],
  );

  const handleItemStatusChange = useCallback(
    async (item, nextStatusId, { undoable = true } = {}) => {
      const previous = item.status;
      if (previous === nextStatusId) return;

      // A return is the whole parcel, so the backend refuses to move one
      // returned line on its own ("A returned item moves with its order").
      // Staff reach for the row's picker first, so a pick there is read as
      // the order-level move it has to be — same prompts, same Undo.
      if (isReturnStatus(previous)) {
        requestOrderStatus(nextStatusId);
        return;
      }

      markItemPending(item.id, true);
      patchItemStatus(item.id, nextStatusId);

      try {
        // `status`, not `status_id` — the same key the order-level
        // admin/orders/status endpoint takes. Sending status_id made the
        // backend reject every per-item change with "Status is required."
        await axiosInstance.patch('admin/orders/detail-status', {
          status: nextStatusId,
          order_detail_id: item.id,
        });
        // A line move can change more than the line: cancelling one restates
        // the totals and the delivery tier, so the order is re-read rather
        // than left with a Payment card that no longer matches the rows.
        await reload({ quiet: true });
        pushToast(
          `${item.product_name || 'Item'} marked ${labelForStatus(nextStatusId)}.`,
          undoable
            ? {
                undo: () =>
                  handleItemStatusChange(
                    { ...item, status: nextStatusId },
                    previous,
                    { undoable: false },
                  ),
              }
            : undefined,
        );
      } catch (err) {
        patchItemStatus(item.id, previous);
        pushToast(readApiError(err, 'Could not update the item status.'), {
          tone: 'danger',
        });
      } finally {
        markItemPending(item.id, false);
      }
    },
    [labelForStatus, markItemPending, patchItemStatus, pushToast, reload, requestOrderStatus],
  );

  /*
   * Tracking numbers, one per trip of the parcel. The server answers every
   * change with the order's full list and the mirrored `tracking_number`, so
   * the screen redraws from that answer instead of merging locally. Adds and
   * corrections offer Undo in the toast; a removal asks first, because the
   * customer may be tracking that very number.
   */
  const applyShipments = useCallback(
    (data) =>
      patchLocal({
        shipments: data?.shipments || [],
        tracking_number: data?.tracking_number ?? null,
      }),
    [patchLocal],
  );

  const removeShipment = useCallback(
    async (shipment) => {
      try {
        const response = await axiosInstance.delete(`${SHIPMENTS_PATH}/${shipment.id}`);
        applyShipments(response?.data?.response);
        pushToast(`${shipmentNoun(shipment.direction)} removed.`);
        return true;
      } catch (err) {
        pushToast(readApiError(err, 'Could not remove the tracking number.'), {
          tone: 'danger',
        });
        return false;
      }
    },
    [applyShipments, pushToast],
  );

  const handleAddShipment = useCallback(
    async (direction, number, { silent = false } = {}) => {
      const current = orderRef.current;
      if (!current) return false;
      try {
        const response = await axiosInstance.post(SHIPMENTS_PATH, {
          id: current.order_id,
          direction,
          tracking_number: number,
        });
        const data = response?.data?.response;
        applyShipments(data);
        // The row just written is the newest one in its direction.
        const added = (data?.shipments || []).find(
          (trip) => trip.direction === direction && trip.tracking_number === number,
        );
        if (!silent) {
          pushToast(
            `${shipmentNoun(direction)} added.`,
            added ? { undo: () => removeShipment(added) } : undefined,
          );
        }
        return true;
      } catch (err) {
        pushToast(readApiError(err, 'Could not add the tracking number.'), {
          tone: 'danger',
        });
        return false;
      }
    },
    [applyShipments, pushToast, removeShipment],
  );

  const handleEditShipment = useCallback(
    async (shipment, number) => {
      const previous = shipment.tracking_number;
      try {
        const response = await axiosInstance.patch(`${SHIPMENTS_PATH}/${shipment.id}`, {
          tracking_number: number,
        });
        applyShipments(response?.data?.response);
        pushToast(`${shipmentNoun(shipment.direction)} saved.`, {
          undo: () => handleEditShipment({ ...shipment, tracking_number: number }, previous),
        });
        return true;
      } catch (err) {
        pushToast(readApiError(err, 'Could not save the tracking number.'), {
          tone: 'danger',
        });
        return false;
      }
    },
    [applyShipments, pushToast],
  );

  const handleRemoveShipment = useCallback(
    (shipment) => {
      const orderNumber = orderRef.current?.order_number || '';
      const customerTrip = shipment.direction === SHIPMENT_DIRECTION.OUTBOUND;
      setConfirmState({
        title: 'Remove this tracking number?',
        description: `${shipmentNoun(shipment.direction)} ${shipment.tracking_number} will be taken off order #${orderNumber}. ${
          customerTrip
            ? 'If the customer is tracking it, their order page goes back to the previous number, or shows none.'
            : 'The customer never sees return numbers.'
        }`,
        confirmLabel: 'Remove',
        run: () => removeShipment(shipment),
      });
    },
    [removeShipment],
  );

  /*
   * The tracking prompt's Confirm: save the trip's number (when one was
   * typed), then make the status move it was asked for. A refused number
   * leaves the box open with the refusal in a toast; the status is never
   * changed without it. One toast at the end, for the move.
   */
  const confirmTrackingPrompt = useCallback(
    async (number) => {
      const prompt = trackingPrompt;
      if (!prompt) return;
      setTrackingPromptPending(true);
      try {
        if (number) {
          const added = await handleAddShipment(prompt.direction, number, { silent: true });
          if (!added) return;
        }
        setTrackingPrompt(null);
        await applyOrderStatus(prompt.statusId, {
          undoable: !isDestructiveStatus(prompt.statusId),
        });
      } finally {
        setTrackingPromptPending(false);
      }
    },
    [applyOrderStatus, handleAddShipment, trackingPrompt],
  );

  const trackingPromptCopyProps = trackingPrompt
    ? trackingPromptCopy({
        direction: trackingPrompt.direction,
        orderNumber: order?.order_number || '',
        statusLabel: labelForStatus(trackingPrompt.statusId),
        courierLabel: COURIER_LABEL,
      })
    : {};

  /*
   * The inline edit modals (name, phone, address, note) still write through
   * redux, which this screen no longer reads — its record lives in useOrder's
   * local state. Without this they saved to the server and the screen sat
   * unchanged, which reads as "it didn't save".
   *
   * Quiet, so the cards don't flash a skeleton for a one-field change.
   */
  const handleFieldSaved = useCallback(
    () => reload({ quiet: true }),
    [reload],
  );

  /*
   * Editing writes return the recomputed money state, so totals update from the
   * response and only the item rows are re-fetched. A refusal is shown verbatim
   * — the backend's messages name the fix ("cancel the whole order instead").
   */
  const editing = useOrderEditing({
    orderId,
    onMoneyChange: patchLocal,
    onItemsChange: () => reload({ quiet: true }),
  });

  const runEdit = useCallback(
    async (action, successMessage) => {
      const result = await action();
      if (result?.ok) {
        if (successMessage) pushToast(successMessage);
      } else {
        pushToast(result?.message || 'That change could not be saved.', {
          tone: 'danger',
        });
      }
      return result;
    },
    [pushToast],
  );

  /*
   * "Hold & edit" from wherever the order happens to be.
   *
   * The backend only accepts hold from New Order or Confirmed, so a dispatched
   * order can't be held directly — but moving it back to Confirmed is an
   * ordinary transition, and holding from there is legal. Rather than telling
   * someone to perform two steps by hand, the button performs both and the
   * confirmation says so, because dropping an order out of Out for delivery is
   * a real change with its own consequences.
   *
   * If the first move is refused, the second is not attempted.
   */
  const handleHoldToEdit = useCallback(() => {
    const current = orderRef.current;
    if (!current) return;
    const needsStepBack = !canEnterHoldFrom(current.status);
    const confirmedLabel = labelForStatus(ORDER_STATUS.CONFIRMED);

    setConfirmState({
      title: needsStepBack
        ? `Move back to ${confirmedLabel} and hold?`
        : 'Put this order on hold to edit it?',
      description: needsStepBack
        ? `An order that is already ${labelForStatus(current.status)} can’t be put on hold directly, so it will be moved back to ${confirmedLabel} first. Tell the courier if it has already left. While it’s on hold, admins who aren’t super admins can’t change anything on it.`
        : 'Editing an order pauses it first, so nobody picks or dispatches it while you work. Admins who aren’t super admins won’t be able to change anything until you resume it.',
      confirmLabel: needsStepBack ? 'Move back & hold' : 'Hold & edit',
      run: async () => {
        if (needsStepBack) {
          const moved = await applyOrderStatus(ORDER_STATUS.CONFIRMED, {
            undoable: false,
          });
          if (!moved) return;
        }
        await applyOrderStatus(ORDER_STATUS.ON_HOLD, { undoable: false });
      },
    });
  }, [applyOrderStatus, labelForStatus]);

  const handleQuantityChange = useCallback(
    (item, quantity) =>
      runEdit(
        () => editing.setItemQuantity({ detailId: item.id, quantity }),
        `${item.product_name || 'Item'} set to ${quantity}.`,
      ),
    [editing, runEdit],
  );

  const handleRemoveItem = useCallback(
    (item) =>
      setConfirmState({
        title: `Remove ${item.product_name || 'this item'}?`,
        description:
          'It stays on the order marked Cancelled, its stock goes back, and the totals are recalculated. Any difference against what was paid shows as a balance.',
        confirmLabel: 'Remove item',
        run: () =>
          runEdit(
            () => editing.removeItem({ detailId: item.id }),
            `${item.product_name || 'Item'} removed.`,
          ),
      }),
    [editing, runEdit],
  );

  const handleAddItem = useCallback(
    ({ productId, quantity }) =>
      runEdit(() => editing.addItem({ productId, quantity }), 'Item added.'),
    [editing, runEdit],
  );

  const handleDeliveryChange = useCallback(
    (amount) =>
      runEdit(
        () => editing.setDeliveryCharge(amount),
        amount > 0
          ? `Delivery set to ${formatAmount(amount)}.`
          : 'Delivery set to free.',
      ),
    [editing, runEdit],
  );

  const existingProductIds = useMemo(
    () =>
      new Set(
        orderItems
          .filter((item) => !isExitStatus(item.status))
          .map((item) => Number(item.product_id)),
      ),
    [orderItems],
  );

  const goBack = useCallback(() => navigate(-1), [navigate]);

  /* ---------- render ---------- */

  const shell =
    'order-view card-head order-card !bg-nysmah-page-bg mt-4 lg:!overflow-hidden lg:flex lg:flex-col';

  if (isError) {
    return (
      <div className={shell}>
        <OrderViewError message={error} onRetry={reload} onBack={goBack} />
      </div>
    );
  }

  if (isLoading || !order) {
    return (
      <div className={shell}>
        <OrderViewSkeleton onBack={goBack} />
      </div>
    );
  }

  return (
    <div className={shell}>
      <OrderCommandBar
        order={order}
        orderId={orderId}
        statusLabel={labelForStatus(order.status)}
        itemCount={orderItems.length}
        hasB2B={derived.hasB2B}
        onBack={goBack}
        nextStep={isEditable && !isHeld ? nextStep : null}
        onAdvance={requestOrderStatus}
        isAdvancing={pendingStatusId != null}
        isEditable={isEditable}
        statusOptions={transitionOptions}
      />

      {isHeld && (
        <HoldBanner
          statusLabel={labelForStatus(order.status)}
          canManageHold={canManageHold}
          onResume={() => requestOrderStatus(ORDER_STATUS.CONFIRMED)}
          isPending={pendingStatusId != null}
        />
      )}

      <OrderLifecycle
        statusId={order.status}
        lifecycle={lifecycle}
        statusLabel={labelForStatus(order.status)}
        stepTimestamps={stepTimestamps}
        isEditable={isEditable}
        pendingStatusId={pendingStatusId}
        onSelect={requestOrderStatus}
      />

      {/* Two independently scrolling panes on wide screens.
        *
        * A sticky rail cannot solve this. Sticky only has somewhere to travel
        * when its container is taller than it is, and here the rail is usually
        * the tallest thing on the page — so it pinned immediately and the only
        * way to reach its lower cards was to scroll the whole page, dragging
        * the items table off-screen with it.
        *
        * Instead the shell stops scrolling at `lg` (`lg:!overflow-hidden` on
        * `.card-head`, which otherwise owns the y-scroll) and this row becomes
        * a flex child that fills the remaining height. Each pane then owns its
        * own scrollport, so the rail scrolls under the cursor without moving
        * the items table, and the command bar and progress track stay put.
        *
        * `min-h-0` on both panes is load-bearing: a flex/grid child defaults to
        * `min-height: auto`, which refuses to shrink below its content and
        * would push the overflow back out to the page. Below `lg` this all
        * collapses to one ordinary scrolling column. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start lg:flex-1 lg:min-h-0">
        <div className="lg:col-span-2 flex flex-col gap-4 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:pr-1.5">
          <OrderItemsTable
            items={orderItems}
            statusOptions={statusOptions}
            isEditable={isEditable}
            canEditContents={editability.canEdit}
            editBlockedReason={editability.reason}
            canOfferHold={editability.canOfferHold}
            onHoldToEdit={handleHoldToEdit}
            pendingItemIds={pendingItemIds}
            isItemSaving={(id) => editing.isPending(`item:${id}`)}
            onItemStatusChange={handleItemStatusChange}
            onQuantityChange={handleQuantityChange}
            onRemoveItem={handleRemoveItem}
            onAddItem={() => setAddItemOpen(true)}
            itemCount={orderItems.length}
            unitCount={derived.unitCount}
          />
          <OrderTimeline entries={order.order_status} />
        </div>

        <aside className="flex flex-col gap-4 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:pr-1.5">
          <PaymentCard
            order={order}
            merchandiseSubtotal={derived.merchandiseSubtotal}
            deliveryCharge={derived.deliveryCharge}
            itemCount={orderItems.length}
            couponDiscount={derived.couponDiscount}
            instantDiscount={derived.instantDiscount}
            canEditContents={editability.canEdit}
            isDeliveryPending={editing.isPending('delivery')}
            onDeliveryChargeChange={handleDeliveryChange}
          />
          <CustomerCard
            order={order}
            orderId={orderId}
            isEditable={isEditable}
            onSaved={handleFieldSaved}
          />
          <DeliveryCard
            order={order}
            orderId={orderId}
            isEditable={isEditable}
            onSaved={handleFieldSaved}
            shipments={shipments}
            courierLabel={COURIER_LABEL}
            onAddShipment={handleAddShipment}
            onEditShipment={handleEditShipment}
            onRemoveShipment={handleRemoveShipment}
          />
          <InternalCard
            order={order}
            orderId={orderId}
            isEditable={isEditable}
            onSaved={handleFieldSaved}
          />
        </aside>
      </div>

      <AddItemDialog
        open={isAddItemOpen}
        onOpenChange={setAddItemOpen}
        existingProductIds={existingProductIds}
        onAdd={handleAddItem}
      />

      <TrackingPromptDialog
        open={trackingPrompt != null}
        onOpenChange={(next) => !next && setTrackingPrompt(null)}
        direction={trackingPrompt?.direction}
        courierLabel={COURIER_LABEL}
        isPending={isTrackingPromptPending}
        onConfirm={confirmTrackingPrompt}
        {...trackingPromptCopyProps}
      />
      <ConfirmDialog
        open={confirmState != null}
        onOpenChange={(next) => !next && setConfirmState(null)}
        title={confirmState?.title}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel}
        isPending={pendingStatusId != null}
        onConfirm={async () => {
          const action = confirmState?.run;
          setConfirmState(null);
          await action?.();
        }}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default OrderView;
